<template>
    <div class="content offline-certification-page">
        <div class="container">
            <!-- 돌아가기 버튼 -->
            <button class="btn-back" @click="goBack">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                돌아가기
            </button>

            <!-- 페이지 타이틀 -->
            <div class="page-title">
                <h2 class="tit">오프라인 인증신청</h2>
            </div>

            <!-- 안내 메시지 -->
            <div class="alert-info">
                <p>인터넷이 연결되지 않은 환경에서 프로그램을 사용하기 위해 오프라인 인증키가 필요합니다.</p>
                <p>프로그램에서 표시된 QR코드를 스캔하여 아래 정보를 입력해주세요.</p>
            </div>

            <!-- 로딩 표시 -->
            <div v-if="state.loading" class="loading-message">
                <p>라이선스 정보를 불러오는 중...</p>
            </div>

            <!-- 폼 -->
            <form @submit.prevent="submitForm" class="offline-cert-form">
                <div class="panel">
                    <div class="panel-header">
                        <div class="left">
                            <strong>인증 정보</strong>
                        </div>
                    </div>

                    <div class="form-content">
                        <!-- UUID -->
                        <div class="form-row necessary">
                            <label class="label" for="uuid">UUID</label>
                            <div class="inp-set">
                                <input 
                                    id="uuid" 
                                    name="uuid" 
                                    type="text" 
                                    placeholder="QR코드에서 자동으로 입력됩니다" 
                                    v-model="formData.uuid" 
                                    required
                                    readonly
                                    class="readonly-input"
                                >
                                <small class="help-text">프로그램에서 표시된 QR코드를 스캔하면 자동으로 입력됩니다.</small>
                                <small class="help-text">해당 UUID는 PC마다 고유하므로, 다른 PC에서는 인증이 유효하지 않습니다.</small>
                            </div>
                        </div>

                        <!-- 제품명 -->
                        <div class="form-row necessary">
                            <label class="label" for="productName">제품명</label>
                            <div class="inp-set">
                                <input 
                                    id="productName" 
                                    name="productName" 
                                    type="text" 
                                    placeholder="제품명을 입력해주세요" 
                                    v-model="formData.productName" 
                                    required
                                    maxlength="100"
                                    readonly
                                    class="readonly-input"
                                >
                            </div>
                        </div>

                        <!-- 제품시리얼넘버 -->
                        <div class="form-row necessary">
                            <label class="label" for="serialNumber">제품시리얼넘버</label>
                            <div class="inp-set">
                                <select 
                                    id="serialNumber" 
                                    name="serialNumber" 
                                    v-model="formData.serialNumber" 
                                    required
                                    @change="onSerialNumberChange"
                                >
                                    <option value="">제품시리얼넘버를 선택해주세요</option>
                                    <option 
                                        v-for="license in state.availableLicenses" 
                                        :key="license.licenseId" 
                                        :value="license.serialNumber"
                                        :disabled="license.alreadyOfflineAssigned || license.hasPendingRequest"
                                    >
                                        {{ license.serialNumber }} - 할당 가능: {{ license.availableCount }}개 - ({{ license.crewName }})
                                        <span v-if="license.alreadyOfflineAssigned"> - [이미 오프라인 인증을 받은 제품]</span>
                                        <span v-else-if="license.hasPendingRequest"> - [승인 대기 중인 신청이 있습니다]</span>
                                    </option>
                                </select>
                                <small class="help-text" v-if="state.availableLicenses.length === 0">
                                    사용 가능한 라이선스가 없습니다.
                                </small>
                                <small class="help-text" v-else>
                                    이미 오프라인 인증을 받은 제품이나 승인 대기 중인 제품은 선택할 수 없습니다.
                                </small>
                            </div>
                        </div>

                        <!-- 인증 유효기간 -->
                        <div class="form-row necessary">
                            <label class="label">인증 유효기간</label>
                            <div class="inp-set">
                                <div class="date-range">
                                    <input 
                                        id="validityEndDate" 
                                        name="validityEndDate" 
                                        type="date" 
                                        v-model="formData.validityEndDate" 
                                        required
                                        :min="formData.validityStartDate"
                                        :max="state.maxEndDate"
                                    >
                                </div>
                                <small class="help-text">
                                    시작일은 오늘 날짜로 고정되며, 종료일은 라이선스 만료일({{ formatDate(state.maxEndDate) }}) 이전으로만 설정 가능합니다.
                                </small>
                            </div>
                        </div>

                        <!-- 오프라인 인증신청 사유 -->
                        <div class="form-row necessary">
                            <label class="label" for="reason">오프라인 인증신청 사유</label>
                            <div class="inp-set">
                                <textarea 
                                    id="reason" 
                                    name="reason" 
                                    placeholder="오프라인 인증이 필요한 사유를 입력해주세요 (예: 인터넷 연결이 불가능한 환경에서 사용)"
                                    v-model="formData.reason" 
                                    required
                                    rows="4"
                                    maxlength="500"
                                ></textarea>
                                <small class="help-text">{{ formData.reason.length }}/500</small>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 제출 버튼 -->
                <div class="btn-area">
                    <button 
                        type="button" 
                        class="btn-submit" 
                        :disabled="state.submitting || state.availableLicenses.length === 0"
                        @click="showWarningModal"
                    >
                        {{ state.submitting ? '처리 중...' : '인증 신청' }}
                    </button>
                </div>
            </form>

            <!-- 주의사항 확인 모달 -->
            <div v-if="state.showWarningModal" class="modal-overlay" @click="closeWarningModal">
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <h3>주의사항</h3>
                        <button class="modal-close" @click="closeWarningModal">×</button>
                    </div>
                    <div class="modal-body">
                        <div class="warning-content">
                            <p>오프라인 인증 요청이 관리자에 의해 승인되면 인증 유효기간 동안 해당 라이선스의 개수가<br>1 BOX 차감됩니다. </p>
                            <p>승인이 처리되면 철회가 불가능하니 유효기간 설정에 유의해 주시기 바랍니다.</p>
                            <p>관리자의 승인 전까지는 [마이페이지>오프라인 인증 신청내역]에서 철회가 가능합니다.</p>
                        </div>
                        <div class="checkbox-wrapper">
                            <label class="checkbox-label">
                                <input 
                                    type="checkbox" 
                                    v-model="state.warningConfirmed"
                                    class="checkbox-input"
                                >
                                <span>주의사항 확인했습니다</span>
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button 
                            class="btn-cancel" 
                            @click="closeWarningModal"
                        >
                            취소
                        </button>
                        <button 
                            class="btn-submit" 
                            :disabled="!state.warningConfirmed || state.submitting"
                            @click="submitForm"
                        >
                            {{ state.submitting ? '처리 중...' : '신청하기' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- 결과 모달 -->
            <div v-if="state.showResultModal" class="modal-overlay" @click="closeResultModal">
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <h3>{{ state.resultModalTitle }}</h3>
                        <button class="modal-close" @click="closeResultModal">×</button>
                    </div>
                    <div class="modal-body">
                        <div v-if="state.resultStatus === 'success'" class="result-success">
                            <p class="result-message">{{ state.resultMessage }}</p>
                        </div>
                        <div v-else class="result-error">
                            <p class="result-message">{{ state.resultMessage }}</p>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn-submit" @click="closeResultModal">확인</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'offlineCertification',
    data() {
        return {
            formData: {
                uuid: '',
                gid: '',
                reason: '',
                productName: '',
                serialNumber: '',
                validityStartDate: '',
                validityEndDate: '',
            },
            state: {
                loading: false,
                submitting: false,
                showWarningModal: false,
                warningConfirmed: false,
                showResultModal: false,
                resultStatus: 'success', // 'success' or 'error'
                resultMessage: '',
                resultModalTitle: '인증 신청 결과',
                resultData: null,
                availableLicenses: [], // 사용 가능한 라이선스 목록
                maxEndDate: '', // 선택된 라이선스의 만료일 (최대 설정 가능한 날짜)
                selectedCrewId: null, // 선택된 라이선스의 crewId
            },
        };
    },
    async created() {
        // 로그인 체크 (비로그인 상태면 로그인 페이지로 리다이렉트)
        if (!this.isLogin()) {
            await this.requireLogin(this, false);
            return;
        }
        
        // 시작일은 항상 오늘로 고정
        const today = new Date();
        this.formData.validityStartDate = this.formatDateForInput(today.toISOString());
        
        // 쿼리 파라미터에서 UUID 가져오기
        this.initUuid();
        
        // UUID와 gid가 있으면 라이선스 정보 조회
        if (this.formData.uuid && this.formData.gid) {
            this.fetchLicenseInfo();
        }
    },
    mounted() {
        // CSS 파일 동적 로드
        if (!document.querySelector('link[href="/ssflex/assets/css/offlineCertification.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = '/ssflex/assets/css/offlineCertification.css';
            document.head.appendChild(link);
        }
    },
    watch: {
        // 라우트 쿼리 파라미터 변경 감지 (로그인 후 돌아왔을 때)
        '$route.query': {
            handler(newQuery) {
                if (newQuery.uuid) {
                    this.initUuid();
                    if (this.formData.uuid && this.formData.gid) {
                        this.fetchLicenseInfo();
                    }
                }
            },
            immediate: true,
        },
    },
    methods: {
        // uuid와 gid 초기화 (쿼리 파라미터에서)
        initUuid() {
            const uuid = this.$route.query.uuid;
            if (uuid) {
                this.formData.uuid = decodeURIComponent(uuid);
            }
            const gid = this.$route.query.gid;
            if (gid) {
                this.formData.gid = gid;
            }
        },

        // uuid로 라이선스 정보 조회
        fetchLicenseInfo() {
            // 로그인 체크
            if (!this.isLogin()) {
                this.requireLogin(this, false);
                return;
            }
            
            if (!this.formData.uuid) {
                return;
            }
            if (!this.formData.gid) {
                this.swalUtils.gritter('GID가 필요합니다.', '', 'warning', 3000);
                return;
            }

            this.state.loading = true;

            this.axiosCaller.post(
                this,
                this.APIs.LICENSE + '/getOfflineCertificationInfo',
                {
                    uuid: this.formData.uuid,
                    gid: this.formData.gid,
                },
                (result) => {
                    const data = result?.data || {};
                    if (data.status === 'ok') {
                        // 제품명 자동 입력
                        this.formData.productName = data.productName || '';
                        
                        // 사용 가능한 라이선스 목록 설정
                        this.state.availableLicenses = data.licenseList || [];
                        
                        // 라이선스가 하나면 자동 선택
                        if (this.state.availableLicenses.length === 1) {
                            const license = this.state.availableLicenses[0];
                            this.formData.serialNumber = license.serialNumber;
                            this.state.selectedCrewId = license.crewId;
                            this.onSerialNumberChange();
                        }
                    } else {
                        this.swalUtils.gritter(data.msg || '라이선스 정보를 불러올 수 없습니다.', '', 'error', 3000);
                    }
                    // 성공/실패 관계없이 로딩 종료
                    this.state.loading = false;
                },
                (error) => {
                    this.swalUtils.gritter('라이선스 정보를 불러오는 중 오류가 발생했습니다.', '', 'error', 3000);
                    // 에러 발생 시에도 로딩 종료
                    this.state.loading = false;
                }
            );
        },

        // 날짜 포맷팅
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('ko-KR');
        },

        // 날짜를 YYYY-MM-DD 형식으로 변환 (input type="date"용)
        formatDateForInput(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        // 제품시리얼넘버 변경 시 호출
        onSerialNumberChange() {
            if (!this.formData.serialNumber) {
                this.state.maxEndDate = '';
                this.formData.validityStartDate = '';
                this.formData.validityEndDate = '';
                this.state.selectedCrewId = null;
                return;
            }

            // 선택된 라이선스 찾기
            const selectedLicense = this.state.availableLicenses.find(
                license => license.serialNumber === this.formData.serialNumber
            );

            if (selectedLicense && selectedLicense.endDate) {
                // 라이선스 만료일을 최대 날짜로 설정
                this.state.maxEndDate = this.formatDateForInput(selectedLicense.endDate);
                
                const today = new Date();
                this.formData.validityStartDate = this.formatDateForInput(today.toISOString());
                this.formData.validityEndDate = this.state.maxEndDate;
                
                // crewId 저장
                this.state.selectedCrewId = selectedLicense.crewId;
            }
        },

        // 돌아가기
        goBack() {
            this.$router.push({ name: 'licenseManagement' });
        },

        // 주의사항 모달 표시
        showWarningModal() {
            // 로그인 체크
            if (!this.isLogin()) {
                this.requireLogin(this, false);
                return;
            }
            
            // 유효성 검사
            if (!this.validateForm()) {
                return;
            }
            
            // 주의사항 모달 표시
            this.state.showWarningModal = true;
            this.state.warningConfirmed = false;
        },

        // 주의사항 모달 닫기
        closeWarningModal() {
            this.state.showWarningModal = false;
            this.state.warningConfirmed = false;
        },

        // 폼 제출
        async submitForm() {
            // 주의사항 확인 체크
            if (!this.state.warningConfirmed) {
                return;
            }
            
            // 주의사항 모달 닫기
            this.state.showWarningModal = false;
            
            // 로그인 체크
            if (!this.isLogin()) {
                await this.requireLogin(this, false);
                return;
            }
            
            // 유효성 검사
            if (!this.validateForm()) {
                return;
            }

            this.state.submitting = true;

            try {
                const today = new Date();
                const todayStr = this.formatDateForInput(today.toISOString());
                
                // 유효기간 종료일을 Unix timestamp로 변환
                let expireTimestamp = null;
                if (this.formData.validityEndDate) {
                    const endDate = new Date(this.formData.validityEndDate);
                    endDate.setHours(23, 59, 59, 999); // 하루의 마지막 시간으로 설정
                    expireTimestamp = Math.floor(endDate.getTime() / 1000);
                }

                const payload = {
                    uuid: this.formData.uuid,
                    gid: this.formData.gid,
                    crewId: this.state.selectedCrewId,
                    reason: this.formData.reason,
                    productName: this.formData.productName,
                    serialNumber: this.formData.serialNumber,
                    validityStartDate: todayStr,
                    validityEndDate: this.formData.validityEndDate,
                    expire: expireTimestamp,
                };

                this.axiosCaller.post(
                    this,
                    this.APIs.LICENSE + '/offlineCertification',
                    payload,
                    (result) => {
                        const data = result?.data || {};
                        if (data.status === 'ok') {
                            this.state.resultStatus = 'success';
                            this.state.resultMessage = '오프라인 인증 신청이 완료되었습니다. 관리자의 승인 후 인증키가 이메일로 발송됩니다.\n[마이페이지 > 오프라인 인증 신청내역]에서 인증 현황을 보실 수 있습니다.';
                            this.state.resultData = data;
                            this.state.showResultModal = true;
                            // 폼 초기화
                            this.resetForm();
                        } else {
                            this.state.resultStatus = 'error';
                            this.state.resultMessage = data.msg || '인증 신청에 실패했습니다.';
                            this.state.showResultModal = true;
                        }
                    },
                    (error) => {
                        this.state.resultStatus = 'error';
                        this.state.resultMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
                        this.state.showResultModal = true;
                    }
                );
            } catch (error) {
                this.state.resultStatus = 'error';
                this.state.resultMessage = '오류가 발생했습니다. 다시 시도해주세요.';
                this.state.showResultModal = true;
            } finally {
                this.state.submitting = false;
            }
        },

        // 폼 유효성 검사
        validateForm() {
            if (!this.formData.uuid) {
                this.swalUtils.gritter('UUID를 입력해주세요.', '', 'warning', 3000);
                return false;
            }
            if (!this.formData.reason || this.formData.reason.trim().length === 0) {
                this.swalUtils.gritter('오프라인 인증신청 사유를 입력해주세요.', '', 'warning', 3000);
                return false;
            }
            if (!this.formData.productName || this.formData.productName.trim().length === 0) {
                this.swalUtils.gritter('제품명을 입력해주세요.', '', 'warning', 3000);
                return false;
            }
            if (!this.formData.serialNumber || this.formData.serialNumber.trim().length === 0) {
                this.swalUtils.gritter('제품시리얼넘버를 선택해주세요.', '', 'warning', 3000);
                return false;
            }
            if (!this.formData.validityEndDate) {
                this.swalUtils.gritter('인증 유효기간 종료일을 선택해주세요.', '', 'warning', 3000);
                return false;
            }
            // 시작일(오늘)이 종료일보다 이후인지 확인
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const endDate = new Date(this.formData.validityEndDate);
            endDate.setHours(0, 0, 0, 0);
            if (today > endDate) {
                this.swalUtils.gritter('종료일은 오늘 이후여야 합니다.', '', 'warning', 3000);
                return false;
            }
            if (this.state.maxEndDate && new Date(this.formData.validityEndDate) > new Date(this.state.maxEndDate)) {
                this.swalUtils.gritter('유효기간 종료일은 라이선스 만료일 이전이어야 합니다.', '', 'warning', 3000);
                return false;
            }
            return true;
        },

        // 폼 초기화
        resetForm() {
            this.formData.reason = '';
            this.formData.productName = '';
            this.formData.serialNumber = '';
            this.formData.validityStartDate = '';
            this.formData.validityEndDate = '';
            this.state.maxEndDate = '';
            this.state.selectedCrewId = null;
            // uuid는 유지
        },

        // 결과 모달 닫기
        closeResultModal() {
            this.state.showResultModal = false;
            this.state.resultData = null;
            if (this.state.resultStatus === 'success') {
                this.$router.push({ name: 'offlineCertificationHistory' });
            }
        },

        // 인증키 복사
        copyAuthKey() {
            if (this.$refs.authKeyInput) {
                this.$refs.authKeyInput.select();
                document.execCommand('copy');
                this.swalUtils.gritter('인증키가 복사되었습니다.', '', 'success', 2000);
            }
        },
    },
};
</script>

<style scoped>
/* 외부 CSS 파일 사용: /ssflex/assets/css/offlineCertification.css */
/* 추가 컴포넌트별 스타일 있을 시 /ssflex/assets/css/offlineCertification.css에 작성*/
</style>
